import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { compactMoney, date, pct } from '../../lib/format';
import { BASIS_LABELS } from '../../lib/labels';
import { sumBy, useTenantScope } from '../../store/selectors';
import { AboutThisPage, PageHeader, StatusChip } from '../../ui/primitives';

export default function FundsPage() {
  const scope = useTenantScope();
  const navigate = useNavigate();
  return (
    <>
      <PageHeader
        title="Funds & classes"
        subtitle="Every fund and unit class on the native register, with its registry configuration."
      />
      <AboutThisPage>
        <p>
          A fund here is the registry view of a trust: its unit classes, pricing basis, distribution
          frequency, bank account and registry rules. Fund marketing content stays in the existing
          Fund Opportunity admin; the registry links to it by fund ID rather than duplicating it.
        </p>
      </AboutThisPage>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' },
        }}
      >
        {scope.funds.map((fund) => {
          const holdings = scope.holdings.filter((holding) => holding.fundId === fund.id);
          const value = sumBy(holdings, (holding) => holding.value);
          const classes = scope.classes.filter((unitClass) => unitClass.fundId === fund.id);
          const holders = new Set(holdings.map((holding) => holding.holderId)).size;
          return (
            <Paper
              key={fund.id}
              variant="outlined"
              onClick={() => navigate(`/manager/funds/${fund.id}`)}
              onKeyDown={(event) => event.key === 'Enter' && navigate(`/manager/funds/${fund.id}`)}
              tabIndex={0}
              role="link"
              sx={{
                p: 2.5,
                cursor: 'pointer',
                '&:hover': { boxShadow: '0 8px 20px rgba(15,23,42,0.08)' },
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    {fund.code} · {fund.strategy}
                  </Typography>
                  <Typography variant="h6">{fund.name}</Typography>
                </Box>
                <StatusChip status={fund.status} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, minHeight: 60 }}>
                {fund.description}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    On register
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>{compactMoney(value)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Holders
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>{holders}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Raised vs target
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {pct((value / fund.targetSize) * 100, 0)}
                  </Typography>
                </Box>
              </Box>
              <Stack
                useFlexGap
                direction="row"
                spacing={1}
                sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}
              >
                <Chip size="small" variant="outlined" label={BASIS_LABELS[fund.basis]} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${fund.distributionFrequency.toLowerCase().replace('_', '-')} distributions`}
                />
                {classes.map((unitClass) => (
                  <Chip
                    key={unitClass.id}
                    size="small"
                    variant="outlined"
                    label={`${unitClass.name} · ${unitClass.targetReturnPct}%`}
                  />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
                {fund.migratedFrom
                  ? `Migrated from ${fund.migratedFrom}; native register since ${date(fund.registryLiveDate)}`
                  : `Native from inception (${date(fund.inceptionDate)})`}
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </>
  );
}
